import {
  faDownload,
  faFolderPlus,
  faTrash,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, message } from 'antd';
import filesize from 'filesize';
import {
  ChangeEventHandler,
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dragDrop from '../../../assets/images/data-room/drag-drop.svg';
import file from '../../../assets/images/data-room/file.svg';
import MainLoader from '../../../components/shared/MainLoader';
import ModalConfirmDelete from '../../../components/shared/ModalConfirmDelete';
import PropertyAssetDropdown from '../../../components/shared/PropertyAssetDropdown';
import ShareWithModal from '../../../components/shared/ShareWithModal';
import ModalWrapper from '../../../components/wrapper/ModalWrapper';
import FeatureState from '../../../context/state/FeatureState';
import useBoolean from '../../../hooks/use-boolean';
import { useUpload } from '../../../hooks/useUpload';
import DataNode from '../../../interfaces/dataRoom/dataNode';
import DataRoomService from '../../../services/DataRoom';
import DraggerDropFile from '../components/dataRoom/DraggerDropFile';
import AddEditItemModal from './dataRoom/AddEditItemModal';
import Breadcrumb, { FileNavigation } from './dataRoom/Breadcrumb';
import DataRoomPlaceholder from './dataRoom/DataRoomPlaceholder';
import { FilePreview } from './dataRoom/File';
import { Action } from './dataRoom/FileMenu';
import SearchBox from './dataRoom/SearchBox';
import TreeNode from './dataRoom/TreeNode';
import PreviewFileModal from './partials/PreviewFileModal';
import { useDownload } from '../../../hooks/useDownload';
const root = 'ROOT';

interface Props {
  editable: boolean;
}

export const DataRoom: FunctionComponent<any> = (props: Props) => {
  const { t } = useTranslation(['f', 'b']),
    [allCheckedFiles, setAllCheckedFiles] = useState<Array<string>>([]),
    [currentNode, setCurrentNode] = useState<DataNode | null>(null),
    [data, setData] = useState<Array<DataNode>>([]),
    [dragEffect, setDraggActions] = useBoolean(),
    [expandedKeysMine] = useState<Array<string>>([]),
    [failedUploads] = useState<Array<any>>([]),
    [fileList, setFileList] = useState<Array<any>>([]),
    [fileName, setFileName] = useState<string | null>(null),
    [fileToPreview, setfileToPreview] = useState<FilePreview | undefined>(),
    [folderNodes, setFolderNodes] = useState<Array<any>>([]),
    [foldersArray, setFoldersArray] = useState<Array<FileNavigation>>([]),
    [isAddEditOpen, setIsAddEditOpen] = useState<boolean>(false),
    [isAddFolder, setIsAddFolder] = useState<boolean>(false),
    [isDeleteModalOpen, setIsDeleteModalOpenActions] = useBoolean(false),
    [isEditable, setIsEditable] = useState<boolean>(false),
    [isLoading, setIsLoadingActions] = useBoolean(),
    [isPermissionsModalOpen, setIsPermissionsModalOpen] =
      useState<boolean>(false),
    [isStringForSearchThere, isStringForSearchThereActions] = useBoolean(),
    [isUploading, setIsUploading] = useState(false),
    [propertyId, setPropertyId] = useState<string>(''),
    [selectedFolderId, setSelectedFolderId] = useState<string>(''),
    [selectedNodeForEdit, setSelectedNodeForEdit] = useState<DataNode | null>(
      null
    ),
    [uploadedFiles] = useState<Array<any>>([]),
    { getFeature } = useContext(FeatureState);
  const { propertySummary } = useContext(FeatureState);
  const { id: propertyAssetid } = propertySummary;
  const { upload, uploadFile } = useUpload();
  const { downloadFiles, downloadSingleFile } = useDownload();
  const modalClosed = () => {
    setIsPermissionsModalOpen(false);
  };

  const fetchData = useCallback(
    async (isFirstLoad: boolean, folderId: string) => {
      setFileList([]);
      if (propertyId) {
        setIsLoadingActions.setTrue();
        if (isFirstLoad) {
          const response = await DataRoomService.fetchDataRoom(propertyId);
          setFolderNodes(response);
          setFoldersArray([]);
          setFileName('');
          setData(response);
        } else {
          refresh();
        }

        setIsLoadingActions.setFalse();
      }
    },
    [propertyId]
  );

  const handleViewFolder = async (folderId: string) => {
    const response = await DataRoomService.fetchDataRoomWithFolderId(
      propertyId,
      folderId
    );
    var folderNode = data.find(n => n.id === folderId)!;
    folderNode.nodes = response;
    foldersArray.push({
      name: folderNode.name,
      id: folderNode.id,
      node: folderNode,
    });
    setCurrentNode(folderNode);
    setSelectedFolderId(folderId);
    setFolderNodes(response);
    setFoldersArray(foldersArray);
    setFileName('');
    setData(response);
  };

  const refresh = async () => {
    if (foldersArray.length === 0) {
      await handleOnClickDataRoom();
    } else {
      await handleRefreshFolder(foldersArray[foldersArray.length - 1].id);
    }
  };

  const handleRefreshFolder = async (folderId: string) => {
    const response = await DataRoomService.fetchDataRoomWithFolderId(
      propertyId,
      folderId
    );
    var folderBreadcrumb = foldersArray.find(n => n.id === folderId)!;
    folderBreadcrumb.node.nodes = response;
    setFolderNodes(response);
    setFileName('');
    setData(response);
  };

  const handleClickFolderBreadcrumb = async (folderId: string) => {
    const response = await DataRoomService.fetchDataRoomWithFolderId(
      propertyId,
      folderId
    );
    var indexFolderBreadcrumb: number = foldersArray.findIndex(
      n => n.id === folderId
    )!;
    var folderBreadcrumb = foldersArray.find(n => n.id === folderId)!;
    folderBreadcrumb.node.nodes = response;
    setCurrentNode(folderBreadcrumb.node);
    setSelectedFolderId(folderId);
    setFolderNodes(response);
    setFoldersArray(foldersArray.slice(0, indexFolderBreadcrumb + 1));
    setFileName('');
    setData(response);
  };

  const handleChange = async info => {
    setFileList([...info.fileList]);
    setIsUploading(true);
    await uploadFile(
      info.fileList?.[0]?.originFileObj,
      selectedFolderId,
      parseInt(propertyId)
    );
    setIsUploading(false);
    refresh();
  };

  const onSelect = (keys: React.Key[]) => {
    setFileName('');
    const id = keys[0].toString();
    setSelectedFolderId(id);
    handleViewFolder(id);
  };

  function handleButtonClick(e) {
    e.stopImmediatePropagation();
    message.info('Click on left button.');
  }

  const handleMenuClick = (e: any, item: DataNode) => {
    if (item.isFolder) setSelectedFolderId(item.id);

    setSelectedNodeForEdit(item);

    if (e.key === Action.Permissions) {
      setIsPermissionsModalOpen(true);
    } else if (e.key === Action.Delete) {
      setIsDeleteModalOpenActions.setTrue();
    } else if (e.key === Action.Edit) {
      setIsAddFolder(false);
      setIsAddEditOpen(true);
    } else if (e.key === Action.AddFolder) {
      setIsAddFolder(true);
      setIsAddEditOpen(true);
    }
  };

  const onSavedAddEdit = () => {
    try {
      refresh();
    } catch (err: any) {
      toast(t(`b:${err.response.date.errorMessage}`));
    }
  };

  const onDragEnd = () => {
    try {
    } catch (err) {
      // toast(t(`b:${err.response.date.errorMessage}`));
    }
  };

  const handleOnCheck = (_key, mee) => {
    mee.node.key === 'upload'
      ? (() => {
          mee.node.checked
            ? setAllCheckedFiles([])
            : setAllCheckedFiles([
                ...folderNodes.map(elem => elem.id),
                mee.node.key,
              ]);
        })()
      : (() => {
          allCheckedFiles.includes(mee.node.key)
            ? setAllCheckedFiles(
                allCheckedFiles.filter(elem => elem !== mee.node.key)
              )
            : setAllCheckedFiles([...allCheckedFiles, mee.node.key]);
        })();
  };

  const handleClickAddFile = useCallback(() => {
    setIsAddFolder(true);
    setSelectedNodeForEdit(currentNode);
    setIsAddEditOpen(true);
  }, [currentNode]);
  const handleBulkDownload = useCallback(() => {
    (async () => {
      if (allCheckedFiles.length > 0) {
        if (
          allCheckedFiles.length === 1 &&
          !data.some(n => n.id === allCheckedFiles[0] && n.isFolder)
        ) {
          const { url } =
            (await downloadSingleFile(
              parseInt(propertyId),
              allCheckedFiles[0]
            )) || {};
          if (url) {
            window.open(url, '_blank');
          }
        } else {
          await downloadFiles(parseInt(propertyId), allCheckedFiles);
        }
      } else {
        await downloadFiles(parseInt(propertyId), [selectedFolderId]);
      }
    })();
  }, [
    data,
    downloadFiles,
    downloadSingleFile,
    propertyId,
    allCheckedFiles,
    selectedFolderId,
  ]);

  const renderButtons = () => {
    return (
      <div className="ml-auto d-flex buttons data-room__buttons-container">
        <Button
          className="button-secondary button-small teams-button"
          onClick={() => {
            document.getElementById('file-uploader')?.click();
          }}
        >
          {t('f:dataRoom.upload')}
          <FontAwesomeIcon
            className="teams-export__icon"
            icon={faUpload}
            size="sm"
          />
        </Button>
        <Button
          className="button-secondary button-small teams-button"
          onClick={handleBulkDownload}
          disabled={isUploading}
        >
          {allCheckedFiles?.length === 1 &&
          !data?.some(n => n.id === allCheckedFiles[0] && n.isFolder)
            ? t('f:dataRoom.download')
            : t('f:dataRoom.bulkDownload')}
          <FontAwesomeIcon
            className="teams-export__icon"
            icon={faDownload}
            size="sm"
          />
        </Button>

        <Button
          className="button-secondary button-small teams-button "
          onClick={async () => {
            for (let i = 0; i < allCheckedFiles.length; i++) {
              await DataRoomService.removeFromDataRoom(allCheckedFiles[i]);
            }
            refresh();
          }}
        >
          {t('f:dataRoom.delete')}
          <FontAwesomeIcon
            className="teams-export__icon"
            icon={faTrash}
            size="sm"
          />
        </Button>
        <Button
          className="button-secondary button-small teams-button"
          onClick={handleClickAddFile}
        >
          {t('f:dataRoom.addFolder')}
          <FontAwesomeIcon
            className="teams-export__icon"
            icon={faFolderPlus}
            size="sm"
          />
        </Button>
      </div>
    );
  };

  const handleDropDownSelect = async (propertyId: string) => {
    setPropertyId(propertyId);
  };
  const findActiveFolderByName = (
    nodes: Array<DataNode>,
    searchString: string,
    data: Array<DataNode>
  ) => {
    for (let i = 0; i < data.length; i++) {
      if (
        data[i].name
          .toLocaleLowerCase()
          .includes(searchString.toLocaleLowerCase())
      ) {
        nodes.push(data[i]);
      }
      const childNodes = Array.isArray(data[i].nodes) ? data[i].nodes : [];
      if (childNodes.length) {
        findActiveFolderByName(nodes, searchString, childNodes);
      }
    }
    return nodes;
  };

  const handleSearchInputChange: ChangeEventHandler<
    HTMLInputElement
  > = event => {
    if (event.currentTarget.value !== '') {
      const nodesContent =
        currentNode === null
          ? data
          : foldersArray[foldersArray.length - 1].node.nodes;
      const nodes: Array<DataNode> = [];
      const filtered = findActiveFolderByName(
        nodes,
        event.currentTarget.value,
        nodesContent
      );
      setFolderNodes(filtered);
      isStringForSearchThereActions.setTrue();
    } else {
      isStringForSearchThereActions.setFalse();
      refresh();
    }
  };

  const handleModalFinish = useCallback(async () => {
    if (selectedNodeForEdit) {
      await DataRoomService.removeFromDataRoom(selectedNodeForEdit.id);
      refresh();
      setSelectedNodeForEdit(null);
    }

    setIsDeleteModalOpenActions.setFalse();
  }, [setIsDeleteModalOpenActions, selectedNodeForEdit]);

  const handleOnClickDataRoom = useCallback(async () => {
    const response = await DataRoomService.fetchDataRoom(propertyId);
    setFolderNodes(response);
    setSelectedFolderId('');
    setFileName('');
    setFoldersArray([]);
    setCurrentNode(null);
    setData(response);
  }, [data]);

  useEffect(() => {
    fetchData(true, '');
    setIsEditable(getFeature('DataRoomEdit'));
  }, [getFeature, fetchData]);

  useEffect(() => {
    setPropertyId(`${propertyAssetid}`);
  }, [propertyAssetid]);

  useEffect(() => {
    setIsUploading(
      uploadedFiles.length + failedUploads.length < fileList.length
    );
  }, [uploadedFiles, failedUploads, fileList.length]);

  useEffect(() => {
    if (!isUploading && uploadedFiles?.length > 0) {
      fetchData(false, selectedFolderId);
    }
  }, [isUploading, fetchData, uploadedFiles?.length]);

  const searchBoxColClassName = isMobile ? 'col-xs-12' : 'col-xs-6';

  return (
    <>
      <div
        className="wrapper"
        onDragEnter={() => {
          setDraggActions.setTrue();
        }}
        onMouseLeave={() => {
          setDraggActions.setFalse();
        }}
      >
        <div className="mx-50 d-flex align-items-center">
          <SearchBox
            className={searchBoxColClassName}
            t={t}
            onChange={handleSearchInputChange}
          />
          {isEditable && !isMobile && renderButtons()}
        </div>
        <div className="col-xs-6 mt-20">
          <PropertyAssetDropdown
            id={`${propertyAssetid}`}
            handleDropDownSelect={handleDropDownSelect}
            classForDrop="col-xs-12-clear col-md-4-clear"
          />
        </div>
        <div className="mx-50 data-room">
          {!isLoading ? (
            data && (data.length > 0 || foldersArray.length > 0) ? (
              <div className="data-room__nodes-container">
                <Breadcrumb
                  folders={foldersArray}
                  onFolderClick={folder => {
                    handleClickFolderBreadcrumb(folder.id);
                  }}
                  t={t}
                  onRootClick={handleOnClickDataRoom}
                  fileName={fileName}
                />
                <TreeNode
                  isEditable={isEditable}
                  onSelect={onSelect}
                  onDragEnd={onDragEnd}
                  expandedKeys={expandedKeysMine}
                  checkedKeys={allCheckedFiles}
                  onCheck={handleOnCheck}
                  isMobile={isMobile}
                  onMenuClick={handleMenuClick}
                  t={t}
                  onFileNameChange={setFileName}
                  onPreview={file => {
                    setfileToPreview(file);
                  }}
                  filesize={filesize}
                  root={root}
                  file={file}
                  folderNodes={folderNodes}
                  onNodeClick={handleButtonClick}
                  propertyId={propertyId}
                  selectedFolderId={selectedFolderId}
                />
              </div>
            ) : (
              <DataRoomPlaceholder
                t={t}
                onAddFile={handleClickAddFile}
                isEditable={isEditable}
              />
            )
          ) : (
            <MainLoader mt="mt-50" type="regular" />
          )}

          {isEditable && (
            <>
              <DraggerDropFile
                imageUrl={dragDrop}
                handleChange={handleChange}
                fileList={fileList}
                failedUploads={failedUploads}
                isUploading={isUploading}
                progress={upload.progress}
                uploadedFiles={uploadedFiles}
                dragEffect={dragEffect}
                folderNodes={folderNodes}
              />
              <div className="modal--container">
                <ModalWrapper
                  isModalOpened={isPermissionsModalOpen}
                  isCloseModalShown={true}
                  closeModalHandler={modalClosed}
                  title={t('f:dataRoom.shareWithModal.giveAccess')}
                  primaryButtonText={t('f:button.cancel')}
                  primaryButtonHandler={modalClosed}
                  isFooterShown={true}
                  className="share-with-modal"
                >
                  <ShareWithModal
                    isModalOpen={isPermissionsModalOpen}
                    nodeId={selectedFolderId}
                  />
                </ModalWrapper>

                <ModalConfirmDelete
                  isModalOpened={isDeleteModalOpen}
                  text={`${t('f:dataRoom.confirmDelete')} ${
                    selectedNodeForEdit
                      ? selectedNodeForEdit.isFolder
                        ? 'folder'
                        : 'file'
                      : ''
                  } ${selectedNodeForEdit ? selectedNodeForEdit.name : ''} ? `}
                  setIsModalOpenedActions={setIsDeleteModalOpenActions}
                  handleFinish={handleModalFinish}
                />

                <AddEditItemModal
                  isModalOpened={isAddEditOpen}
                  setIsModalOpened={setIsAddEditOpen}
                  propertyId={propertyId}
                  id={isAddFolder ? null : selectedNodeForEdit?.id}
                  initialName={isAddFolder ? '' : selectedNodeForEdit?.name}
                  parentId={isAddFolder ? selectedFolderId : null}
                  onSaved={onSavedAddEdit}
                  title={
                    isAddFolder
                      ? t(`f:dataRoom.addFolder`)
                      : t(`f:dataRoom.removeFolder`)
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>
      <PreviewFileModal fileData={fileToPreview} />
    </>
  );
};

export default DataRoom;
